import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminListMedia, createMedia, updateMedia, deleteMedia, uploadMediaFile } from '../../services/media';
import { adminTranslate } from '../../services/admin';
import { Button } from '../../components/ui/button';
import MediaPicker from './components/MediaPicker';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

const TYPES = [
  { value: 'blog_post', label: 'Article de blog' },
  { value: 'tract', label: 'Tract' },
  { value: 'article', label: 'Article' },
  { value: 'documentary', label: 'Documentaire' },
  { value: 'shop_item', label: 'Produit boutique' }
];

const LANGS = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'ar', label: 'العربية' },
  { value: 'it', label: 'Italiano' },
  { value: 'de', label: 'Deutsch' },
];

const emptyForm = { title: '', description: '', category: '', tags: '', content: '', excerpt: '', image_url: '' };

// TipTap wrapper avec upload image, liens & médiathèque
const RichText = ({ value, onChange, onUploadImage, onPickFromLibrary }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: true }),
      Image,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] })
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML())
  });

  const fileInputId = useMemo(() => `rt-file-${Math.random().toString(36).slice(2)}`, []);
  const onPickImage = () => {
    const el = document.getElementById(fileInputId);
    if (el) el.click();
  };
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await onUploadImage?.(f);
      if (url) editor?.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      // noop
    } finally {
      e.target.value = '';
    }
  };
  const addLink = () => {
    const previous = editor?.getAttributes('link').href;
    const url = window.prompt('Entrer une URL', previous || 'https://');
    if (url === null) return; // cancel
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border rounded">
      <input id={fileInputId} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
      <div className="flex flex-wrap gap-2 p-2 border-b">
        {/* Actions inline + médiathèque */}
        <button aria-label="Gras" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleBold().run()}>Gras</button>
        <button aria-label="Italique" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleItalic().run()}>Italique</button>
        <button aria-label="Souligné" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleUnderline().run()}>Souligné</button>
        <button aria-label="Liste" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleBulletList().run()}>Liste</button>
        <button aria-label="Liste numérotée" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleOrderedList().run()}>Liste numérotée</button>
        <button aria-label="Paragraphe" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().setParagraph().run()}>Paragraphe</button>
        <button aria-label="H2" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
        <button aria-label="H3" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>H3</button>
        <button aria-label="Aligner à gauche" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().setTextAlign('left').run()}>Gauche</button>
        <button aria-label="Centrer" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().setTextAlign('center').run()}>Centre</button>
        <button aria-label="Aligner à droite" type="button" className="px-2 py-1 text-sm border rounded" onClick={() => editor?.chain().focus().setTextAlign('right').run()}>Droite</button>
        <button aria-label="Lien" type="button" className="px-2 py-1 text-sm border rounded" onClick={addLink}>Lien</button>
        <button aria-label="Insérer une image" type="button" className="px-2 py-1 text-sm border rounded" onClick={onPickImage}>Image</button>
        <button aria-label="Ouvrir la médiathèque" type="button" className="px-2 py-1 text-sm border rounded" onClick={onPickFromLibrary}>Médiathèque</button>
      </div>
      <EditorContent editor={editor} className="min-h-[220px] p-3" />
    </div>
  );
};

const ContentEditor = () => {
  const { user, token } = useAuth();
  const [type, setType] = useState('tract');
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [listSortBy, setListSortBy] = useState('updated_desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState('draft');
  const [formByLang, setFormByLang] = useState({ fr: { ...emptyForm } });
  const [seoByLang, setSeoByLang] = useState({ fr: { title: '', description: '', og_image: '' } });
  const [currentLang, setCurrentLang] = useState('fr');
  const [autoTranslateOnPublish, setAutoTranslateOnPublish] = useState(true);
  const [openPicker, setOpenPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { if (user && user.user_type === 'fondateur') loadItems(); }, [user, type, q]);

  const loadItems = async () => {
    try {
      setLoading(true); setError('');
      const list = await adminListMedia({ media_type: type, q, limit: 200 }, token);
      setItems(list);
      if (list.length && !selected) setSelected(list[0]);
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (selected) {
      const base = {
        title: selected.title || '',
        description: selected.description || '',
        category: selected.category || '',
        tags: (selected.tags || []).join(','),
        content: selected.content || '',
        excerpt: selected.excerpt || '',
        image_url: selected.image_url || ''
      };
      const translations = (selected.metadata && selected.metadata.translations) || {};
      const seo = (selected.metadata && selected.metadata.seo) || {};
      const next = { fr: { ...emptyForm, ...base } };
      const nextSeo = { fr: { title: seo?.fr?.title || '', description: seo?.fr?.description || '', og_image: seo?.fr?.og_image || '' } };
      for (const lang of LANGS.map(l => l.value)) {
        if (lang === 'fr') continue;
        const t = translations[lang] || {};
        next[lang] = {
          title: t.title || '',
          description: t.description || '',
          category: base.category || '',
          tags: (selected.tags || []).join(','),
          content: t.content || '',
          excerpt: t.excerpt || '',
          image_url: selected.image_url || ''
        };
        nextSeo[lang] = { title: seo?.[lang]?.title || '', description: seo?.[lang]?.description || '', og_image: seo?.[lang]?.og_image || '' };
      }
      setFormByLang(next);
      setSeoByLang(nextSeo);
      setStatus(selected.status || 'draft');
    } else {
      setFormByLang({ fr: { ...emptyForm } });
      setSeoByLang({ fr: { title: '', description: '', og_image: '' } });
      setStatus('draft');
    }
  }, [selected]);

  if (!user || user.user_type !== 'fondateur') return <div className="p-6 text-red-500">Accès réservé au fondateur</div>;

  const setField = (lang, key, val) => setFormByLang(prev => ({ ...prev, [lang]: { ...prev[lang], [key]: val } }));
  const setSeoField = (lang, key, val) => setSeoByLang(prev => ({ ...prev, [lang]: { ...prev[lang], [key]: val } }));
  const current = formByLang[currentLang] || emptyForm;
  const currentSeo = seoByLang[currentLang] || { title: '', description: '', og_image: '' };

  const onNew = () => { setSelected(null); setFormByLang({ fr: { ...emptyForm } }); setSeoByLang({ fr: { title: '', description: '', og_image: '' } }); setCurrentLang('fr'); setStatus('draft'); };

  const buildPayload = () => {
    const fr = formByLang.fr || emptyForm;
    const translations = {};
    const seo = {};
    for (const lang of LANGS.map(l => l.value)) {
      const v = formByLang[lang];
      const s = seoByLang[lang];
      if (lang !== 'fr' && v && (v.title || v.description || v.content || v.excerpt)) {
        translations[lang] = { title: v.title, description: v.description, content: v.content, excerpt: v.excerpt };
      }
      if (s && (s.title || s.description || s.og_image)) {
        seo[lang] = { title: s.title || '', description: s.description || '', og_image: s.og_image || '' };
      }
    }
    const base = {
      title: fr.title,
      description: fr.description || undefined,
      media_type: type,
      status: status,
      image_url: fr.image_url || undefined,
      tags: fr.tags ? fr.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      category: fr.category || undefined,
      content: fr.content || undefined,
      excerpt: fr.excerpt || undefined,
      metadata: { translations, seo }
    };
    if (type === 'shop_item') {
      return {
        ...base,
        price: Number(isNaN(Number(fr.price)) ? 0 : Number(fr.price)),
        currency: fr.currency || 'EUR',
        stock_quantity: fr.stock_quantity !== undefined && fr.stock_quantity !== '' ? Number(fr.stock_quantity) : 0,
        in_stock: fr.in_stock !== undefined ? !!fr.in_stock : true,
        sku: fr.sku || undefined
      };
    }
    return base;
  };

  const performAutoTranslateAll = async () => {
    try {
      const fr = formByLang.fr || emptyForm;
      const texts = [fr.title, fr.description, fr.content, fr.excerpt];
      for (const lang of LANGS.map(l => l.value)) {
        if (lang === 'fr') continue;
        const res = await adminTranslate({ texts, target_lang: lang, source_lang: 'fr' }, token);
        const [tTitle, tDesc, tContent, tExcerpt] = res.translated_texts || [];
        setFormByLang(prev => ({
          ...prev,
          [lang]: {
            ...(prev[lang] || emptyForm),
            title: tTitle || '',
            description: tDesc || '',
            content: tContent || '',
            excerpt: tExcerpt || '',
            category: fr.category,
            tags: fr.tags,
            image_url: fr.image_url
          }
        }));
      }
      setSuccess('✅ Traduction automatique appliquée');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const onTranslateCurrent = async () => {
    try {
      setError(''); setSuccess('');
      const fr = formByLang.fr || emptyForm;
      if (currentLang === 'fr') { setError('Sélectionnez une langue cible ≠ FR.'); return; }
      const texts = [fr.title, fr.description, fr.content, fr.excerpt];
      const res = await adminTranslate({ texts, target_lang: currentLang, source_lang: 'fr' }, token);
      const [tTitle, tDesc, tContent, tExcerpt] = res.translated_texts || [];
      setFormByLang(prev => ({
        ...prev,
        [currentLang]: {
          ...(prev[currentLang] || emptyForm),
          title: tTitle || '',
          description: tDesc || '',
          content: tContent || '',
          excerpt: tExcerpt || '',
          category: fr.category,
          tags: fr.tags,
          image_url: fr.image_url
        }
      }));
      setSuccess(`✅ Contenu traduit en ${currentLang.toUpperCase()}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const onSave = async (publish = false) => {
    try {
      setError(''); setSuccess('');
      let finalStatus = publish ? 'published' : status;
      if (publish && autoTranslateOnPublish) {
        await performAutoTranslateAll();
      }
      const payload = buildPayload();
      payload.status = finalStatus;
      if (selected) {
        const updated = await updateMedia(selected.id, payload, token);
        setSelected(updated);
        setSuccess(publish ? '✅ Publié' : '✅ Modifications enregistrées');
      } else {
        const created = await createMedia(payload, token);
        setSelected(created);
        setSuccess(publish ? '✅ Créé et publié' : '✅ Contenu créé');
      }
      await loadItems();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const onDelete = async () => {
    if (!selected) return;
    await deleteMedia(selected.id, token);
    setSelected(null);
    await loadItems();
  };

  const onUploadCover = async (file) => {
    const res = await uploadMediaFile(file, token);
    setField('fr', 'image_url', res.url);
  };

  const onUploadInlineImage = async (file) => {
    const res = await uploadMediaFile(file, token);
    return res.url;
  };

  const onUploadOgImage = async (file) => {
    const res = await uploadMediaFile(file, token);
    setSeoField(currentLang, 'og_image', res.url);
  };

  const onPickFromLibrary = () => setOpenPicker(true);

  // Derived list with filters/sort/pagination
  const filtered = items.filter(it => {
    const matchQ = !q || (it.title || '').toLowerCase().includes(q.toLowerCase());
    const matchStatus = statusFilter === 'all' || (it.status || 'draft') === statusFilter;
    return matchQ && matchStatus;
  });
  const sorted = [...filtered].sort((a, b) => {
    switch (listSortBy) {
      case 'updated_asc':
        return new Date(a.updated_at) - new Date(b.updated_at);
      case 'title_az':
        return (a.title || '').localeCompare(b.title || '');
      default:
        return new Date(b.updated_at) - new Date(a.updated_at);
    }
  });
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>Contenus</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-3">
              <select aria-label="Type de contenu" className="border rounded px-2 py-1" value={type} onChange={e => { setType(e.target.value); setPage(1);} }>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input aria-label="Recherche" className="flex-1 border rounded px-2 py-1" placeholder="Recherche" value={q} onChange={e => { setQ(e.target.value); setPage(1);} } />
              <select aria-label="Filtrer par statut" className="border rounded px-2 py-1" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1);} }>
                <option value="all">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="archived">Archivé</option>
              </select>
              <select aria-label="Trier" className="border rounded px-2 py-1" value={listSortBy} onChange={e => setListSortBy(e.target.value)}>
                <option value="updated_desc">Récents d'abord</option>
                <option value="updated_asc">Anciens d'abord</option>
                <option value="title_az">Titre A→Z</option>
              </select>
              <Button onClick={onNew}>Nouveau</Button>
            </div>
            <div className="max-h-[520px] overflow-auto space-y-2">
              {paged.map(it => (
                <div key={it.id} className={`border rounded p-2 cursor-pointer ${selected?.id === it.id ? 'border-primary' : 'border-border'}`} onClick={() => setSelected(it)}>
                  <div className="text-sm font-medium line-clamp-1 flex items-center justify-between">
                    <span>{it.title || 'Sans titre'}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${it.status === 'published' ? 'border-green-500 text-green-500' : it.status === 'archived' ? 'border-yellow-500 text-yellow-500' : 'border-gray-400 text-gray-400'}`}>{it.status || 'draft'}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(it.updated_at).toLocaleString('fr-FR')}</div>
                </div>
              ))}
              {paged.length === 0 && <div className="text-sm text-muted-foreground">Aucun contenu</div>}
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-muted-foreground">Page {page} / {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Précédent</Button>
                <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Suivant</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>{selected ? 'Éditer' : 'Nouveau'} {TYPES.find(t => t.value === type)?.label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && <Alert><AlertDescription className="text-red-600">{error}</AlertDescription></Alert>}
            {success && <Alert><AlertDescription className="text-green-600">{success}</AlertDescription></Alert>}

            <div className="flex flex-wrap gap-2">
              {LANGS.map(l => (
                <button key={l.value} onClick={() => setCurrentLang(l.value)} className={`px-3 py-1 rounded text-sm border ${currentLang === l.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted hover:bg-muted/80'}`}>{l.label}</button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <label className="text-sm flex items-center gap-2" data-testid="auto-translate-toggle"><input type="checkbox" checked={autoTranslateOnPublish} onChange={e => setAutoTranslateOnPublish(e.target.checked)} />Traduire automatiquement à la publication</label>
                <Button variant="outline" onClick={onTranslateCurrent}>Traduire en {currentLang.toUpperCase()}</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm mb-1">Statut</label>
                <select className="w-full border rounded px-3 py-2" value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="draft">Brouillon</option>
                  <option value="published">Publié</option>
                  <option value="archived">Archivé</option>
                </select>
              </div>
              {type === 'shop_item' && (
                <>
                  <div>
                    <label className="block text-sm mb-1">Prix</label>
                    <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={formByLang.fr?.price || ''} onChange={e => setField('fr', 'price', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Devise</label>
                    <input className="w-full border rounded px-3 py-2" value={formByLang.fr?.currency || 'EUR'} onChange={e => setField('fr', 'currency', e.target.value)} />
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm mb-1">Titre ({currentLang.toUpperCase()})</label>
                <input className="w-full border rounded px-3 py-2" value={current.title} onChange={e => setField(currentLang, 'title', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm mb-1">Catégorie</label>
                <input className="w-full border rounded px-3 py-2" value={current.category} onChange={e => setField(currentLang, 'category', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Description ({currentLang.toUpperCase()})</label>
                <textarea rows="2" className="w-full border rounded px-3 py-2" value={current.description} onChange={e => setField(currentLang, 'description', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Contenu ({currentLang.toUpperCase()})</label>
                <RichText value={current.content} onChange={(val) => setField(currentLang, 'content', val)} onUploadImage={onUploadInlineImage} onPickFromLibrary={onPickFromLibrary} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Extrait ({currentLang.toUpperCase()})</label>
                <textarea rows="2" className="w-full border rounded px-3 py-2" value={current.excerpt} onChange={e => setField(currentLang, 'excerpt', e.target.value)} />
              </div>
              {type === 'shop_item' && (
                <>
                  <div>
                    <label className="block text-sm mb-1">Stock</label>
                    <input type="number" className="w-full border rounded px-3 py-2" value={formByLang.fr?.stock_quantity || ''} onChange={e => setField('fr', 'stock_quantity', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">SKU</label>
                    <input className="w-full border rounded px-3 py-2" value={formByLang.fr?.sku || ''} onChange={e => setField('fr', 'sku', e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <input id="in_stock" type="checkbox" checked={!!formByLang.fr?.in_stock} onChange={e => setField('fr', 'in_stock', e.target.checked)} />
                    <label htmlFor="in_stock" className="text-sm">Disponible (en stock)</label>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm mb-1">Tags (séparés par des virgules)</label>
                <input className="w-full border rounded px-3 py-2" value={current.tags} onChange={e => setField(currentLang, 'tags', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm mb-1">Image (optionnel)</label>
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && onUploadCover(e.target.files[0])} />
                  {current.image_url && <img src={current.image_url} alt="preview" className="h-16 rounded" />}
                </div>
              </div>
            </div>

            {/* SEO per language */}
            <div className="border rounded p-3">
              <div className="font-medium mb-2">SEO ({currentLang.toUpperCase()})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm mb-1">Meta Title</label>
                  <input className="w-full border rounded px-3 py-2" value={currentSeo.title} onChange={e => setSeoField(currentLang, 'title', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm mb-1">Meta Description</label>
                  <input className="w-full border rounded px-3 py-2" value={currentSeo.description} onChange={e => setSeoField(currentLang, 'description', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">OG Image</label>
                  <div className="flex items-center gap-3">
                    <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && onUploadOgImage(e.target.files[0])} />
                    {currentSeo.og_image && <img src={currentSeo.og_image} alt="og" className="h-12 rounded" />}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div className="flex gap-2">
                <Button onClick={() => onSave(false)}>Enregistrer brouillon</Button>
                <Button onClick={() => onSave(true)}>Publier</Button>
              </div>
              {selected && <Button variant="outline" onClick={onDelete}>Supprimer</Button>}
            </div>

            {/* Media Picker modal for inline images */}
            <MediaPicker open={openPicker} onClose={() => setOpenPicker(false)} onSelect={(url) => {
              const currentHTML = formByLang[currentLang]?.content || '';
              const nextHTML = `${currentHTML}<p><img src="${url}" /></p>`;
              setField(currentLang, 'content', nextHTML);
              setOpenPicker(false);
            }} mediaType="gallery_image" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContentEditor;